import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, getToken, formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { HelpButton } from "@/components/HelpButton";
import { PAGE, INPUT } from "@/lib/styles";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  FileText, Network, Table2, FileType2, Folder, Search, LayoutGrid, List,
  Clock, MoreVertical, Plus, ChevronRight, FolderOpen, HardDrive, Trash2,
  Download, FolderInput, X, Pencil, Loader2, Wallet, Landmark, Scale,
} from "lucide-react";

const TYPES = {
  text:    { label: "Nota", route: (id) => `/note/${id}`,    Icon: FileText,  cls: "text-sky-600 dark:text-sky-400",       bg: "bg-sky-50 dark:bg-sky-500/10",     ring: "border-sky-200 dark:border-sky-500/30" },
  mindmap: { label: "Mapa", route: (id) => `/mindmap/${id}`, Icon: Network,   cls: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-50 dark:bg-indigo-500/10", ring: "border-indigo-200 dark:border-indigo-500/30" },
  sheet:   { label: "Hoja", route: (id) => `/hoja/${id}`,    Icon: Table2,    cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10", ring: "border-emerald-200 dark:border-emerald-500/30" },
  movemap: { label: "Flujo económico", route: (id) => `/movimientos/${id}`, Icon: Wallet, cls: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-500/10", ring: "border-orange-200 dark:border-orange-500/30" },
  board:   { label: "Pizarra", route: (id) => `/pizarra/${id}`, Icon: Landmark, cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10", ring: "border-emerald-200 dark:border-emerald-500/30" },
  ledger:  { label: "Contable", route: (id) => `/contable/${id}`, Icon: Scale, cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10", ring: "border-emerald-200 dark:border-emerald-500/30" },
  pdf:     { label: "PDF",  route: (id) => `/editar-pdf/${id}`, Icon: FileType2, cls: "text-rose-600 dark:text-rose-400",  bg: "bg-rose-50 dark:bg-rose-500/10",   ring: "border-rose-200 dark:border-rose-500/30" },
};

const FILTERS = [
  { key: "all", label: "Todos" },
  { key: "text", label: "Notas" },
  { key: "mindmap", label: "Mapas" },
  { key: "sheet", label: "Hojas" },
  { key: "movemap", label: "Flujo económico" },
  { key: "board", label: "Pizarras" },
  { key: "ledger", label: "Contable" },
  { key: "pdf", label: "PDFs" },
];

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(ts) {
  if (!ts) return "-";
  try { return format(new Date(ts), "d 'de' MMMM yyyy · HH:mm", { locale: es }); } catch { return "-"; }
}

function FileIcon({ type, size = 22 }) {
  const t = TYPES[type] || TYPES.text;
  const Icon = t.Icon;
  return (
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${t.bg} border ${t.ring}`}>
      <Icon size={size} className={t.cls} />
    </div>
  );
}

export default function MisArchivos() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState(() => localStorage.getItem("notacora_archivos_view") || "grid");
  const [filter, setFilter] = useState("all");
  const [folder, setFolder] = useState("__all__");
  const [q, setQ] = useState("");
  const [menu, setMenu] = useState(null); // item id with open menu
  const [moveTarget, setMoveTarget] = useState(null); // item being moved
  const [viewer, setViewer] = useState(null); // pdf viewer {id, name}

  const setViewMode = (v) => { setView(v); localStorage.setItem("notacora_archivos_view", v); };

  const load = useCallback(async () => {
    try {
      const [notesRes, docsRes, foldersRes] = await Promise.all([
        api.get("/notes"),
        api.get("/docs/files"),
        api.get("/docs/folders"),
      ]);
      const notes = notesRes.data.map((n) => ({
        id: n.id, kind: "note", type: n.type || "text", name: n.title || "Sin título",
        folder_id: n.folder_id || null, ts: n.updated_at, size: null,
      }));
      const docs = docsRes.data.map((d) => ({
        id: d.id, kind: "doc", type: "pdf", name: d.filename,
        folder_id: d.folder_id || null, ts: d.created_at, size: d.size,
      }));
      const all = [...notes, ...docs].sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
      setItems(all);
      setFolders(foldersRes.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const folderName = useMemo(() => {
    if (folder === "__all__") return "Todos";
    if (folder === "") return "Sin carpeta";
    return folders.find((f) => f.id === folder)?.name || "Carpeta";
  }, [folder, folders]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((it) => {
      if (filter !== "all" && it.type !== filter) return false;
      if (folder === "") { if (it.folder_id) return false; }
      else if (folder !== "__all__") { if (it.folder_id !== folder) return false; }
      if (ql && !it.name.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [items, filter, folder, q]);

  const folderCounts = useMemo(() => {
    const map = {};
    for (const it of items) {
      const key = it.folder_id || "__none__";
      map[key] = (map[key] || 0) + 1;
    }
    return map;
  }, [items]);

  const openItem = (it) => {
    if (it.type === "pdf") { setViewer({ id: it.id, name: it.name }); return; }
    navigate((TYPES[it.type] || TYPES.text).route(it.id));
  };

  const removeItem = async (it) => {
    if (!window.confirm(`¿Enviar "${it.name}" a la papelera?`)) return;
    try {
      await api.delete(it.kind === "doc" ? `/docs/files/${it.id}` : `/notes/${it.id}`);
      setItems((arr) => arr.filter((x) => !(x.id === it.id && x.kind === it.kind)));
      setMenu(null);
      toast.success("Enviado a la papelera");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const doMove = async (targetFolderId) => {
    const it = moveTarget;
    try {
      if (it.kind === "doc") await api.patch(`/docs/files/${it.id}`, { folder_id: targetFolderId });
      else await api.put(`/notes/${it.id}`, { folder_id: targetFolderId });
      setItems((arr) => arr.map((x) => (x.id === it.id && x.kind === it.kind ? { ...x, folder_id: targetFolderId || null } : x)));
      setMoveTarget(null);
      toast.success("Movido");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const createFolder = async () => {
    const name = window.prompt("Nombre de la nueva carpeta:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await api.post("/docs/folders", { name: name.trim() });
      setFolders((f) => [...f, { ...data }].sort((a, b) => a.name.localeCompare(b.name)));
      toast.success("Carpeta creada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const downloadPdf = (it) => {
    const a = document.createElement("a");
    a.href = `${API}/docs/files/${it.id}/download?auth=${getToken()}`;
    a.download = it.name;
    a.click();
    setMenu(null);
  };

  return (
    <div className={`${PAGE} font-body`} data-testid="mis-archivos-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Sidebar de carpetas */}
        <aside className="hidden lg:flex flex-col gap-1">
          <p className="text-[11px] uppercase tracking-wider font-mono2 text-slate-400 px-3 mb-2">Carpetas</p>
          <FolderBtn active={folder === "__all__"} onClick={() => setFolder("__all__")} icon={HardDrive} label="Todos" count={items.length} testid="folder-all" />
          {folders.map((f) => (
            <FolderBtn key={f.id} active={folder === f.id} onClick={() => setFolder(f.id)}
              icon={folder === f.id ? FolderOpen : Folder} label={f.name} count={folderCounts[f.id] || 0} testid={`folder-${f.id}`} />
          ))}
          <FolderBtn active={folder === ""} onClick={() => setFolder("")} icon={Folder} label="Sin carpeta" count={folderCounts["__none__"] || 0} testid="folder-none" />
          <div className="h-px bg-slate-200 dark:bg-slate-800 my-3" />
          <button onClick={createFolder} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors" data-testid="new-folder-button">
            <Plus size={17} /> Nueva carpeta
          </button>
        </aside>

        {/* Panel principal */}
        <main>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight flex items-center gap-2">
                Mis Archivos <HelpButton tool="getting-started" />
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                <span>Todo tu contenido en un solo lugar</span>
                <ChevronRight size={14} /> <span className="text-slate-700 dark:text-slate-300">{folderName}</span>
                <span className="font-mono2 text-xs">· {filtered.length}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input className={`${INPUT} pl-9 w-full sm:w-56`} placeholder="Buscar archivos..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="archivos-search" />
              </div>
              <div className="flex items-center rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden shrink-0">
                <button onClick={() => setViewMode("grid")} className={`p-2.5 transition-colors ${view === "grid" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`} data-testid="view-grid"><LayoutGrid size={17} /></button>
                <button onClick={() => setViewMode("list")} className={`p-2.5 transition-colors ${view === "list" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`} data-testid="view-list"><List size={17} /></button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-6">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-all ${filter === f.key ? "bg-slate-900 dark:bg-white text-white dark:text-slate-900" : "border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700"}`}
                data-testid={`filter-${f.key}`}>
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="font-mono2 text-sm text-slate-400">Cargando...</p>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-slate-300 dark:border-slate-700 rounded-2xl p-16 text-center" data-testid="archivos-empty">
              <HardDrive size={40} className="mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="font-heading font-normal text-xl mb-2">No hay archivos aquí</h3>
              <p className="font-body text-sm text-slate-500 dark:text-slate-400">Crea notas, mapas, hojas o sube PDFs y aparecerán aquí.</p>
            </div>
          ) : view === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="archivos-grid">
              {filtered.map((it) => (
                <div key={`${it.kind}-${it.id}`}
                  className="group relative bg-slate-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 transition-all duration-300 hover:-translate-y-1 hover:border-indigo-300 dark:hover:border-indigo-500/40 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-black/30 cursor-pointer"
                  onClick={() => openItem(it)} data-testid={`file-card-${it.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <FileIcon type={it.type} />
                    <RowMenu it={it} open={menu === `${it.kind}-${it.id}`} setOpen={(v) => setMenu(v ? `${it.kind}-${it.id}` : null)}
                      onMove={() => { setMoveTarget(it); setMenu(null); }} onDelete={() => removeItem(it)} onDownload={() => downloadPdf(it)} onEdit={() => navigate((TYPES[it.type] || TYPES.text).route(it.id))} />
                  </div>
                  <h3 className="font-medium text-sm text-slate-800 dark:text-slate-100 truncate mb-1">{it.name}</h3>
                  <div className="flex items-center justify-between text-xs text-slate-400 dark:text-slate-500">
                    <span className="flex items-center gap-1"><Clock size={12} /> {fmtDate(it.ts)}</span>
                    <span className="font-mono2">{it.size ? fmtSize(it.size) : (TYPES[it.type] || TYPES.text).label}</span>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono2 text-slate-400">
                    <Folder size={11} /> {it.folder_id ? (folders.find((f) => f.id === it.folder_id)?.name || "Carpeta") : "Sin carpeta"}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden" data-testid="archivos-list">
              <div className="grid grid-cols-[1fr_120px_120px_70px_40px] gap-4 px-5 py-3 text-[11px] uppercase tracking-wider font-mono2 text-slate-400 border-b border-slate-200 dark:border-slate-800">
                <span>Nombre</span><span>Carpeta</span><span>Modificado</span><span>Tamaño</span><span></span>
              </div>
              {filtered.map((it) => (
                <div key={`${it.kind}-${it.id}`} className="grid grid-cols-[1fr_120px_120px_70px_40px] gap-4 px-5 py-3 items-center text-sm hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors cursor-pointer border-b border-slate-100 dark:border-slate-800/60 last:border-0"
                  onClick={() => openItem(it)} data-testid={`file-row-${it.id}`}>
                  <span className="flex items-center gap-3 min-w-0">
                    <FileIcon type={it.type} size={16} />
                    <span className="truncate text-slate-800 dark:text-slate-100">{it.name}</span>
                  </span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs truncate">{it.folder_id ? (folders.find((f) => f.id === it.folder_id)?.name || "Carpeta") : "—"}</span>
                  <span className="text-slate-500 dark:text-slate-400 text-xs">{fmtDate(it.ts)}</span>
                  <span className="text-slate-400 text-xs font-mono2">{it.size ? fmtSize(it.size) : "—"}</span>
                  <RowMenu it={it} open={menu === `${it.kind}-${it.id}`} setOpen={(v) => setMenu(v ? `${it.kind}-${it.id}` : null)}
                    onMove={() => { setMoveTarget(it); setMenu(null); }} onDelete={() => removeItem(it)} onDownload={() => downloadPdf(it)} onEdit={() => navigate((TYPES[it.type] || TYPES.text).route(it.id))} />
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Modal mover a carpeta */}
      {moveTarget && (
        <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setMoveTarget(null)} data-testid="move-modal">
          <div className="w-full max-w-sm bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-medium text-lg flex items-center gap-2"><FolderInput size={18} className="text-indigo-500" /> Mover a carpeta</h3>
              <button onClick={() => setMoveTarget(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"><X size={16} /></button>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4 truncate">"{moveTarget.name}"</p>
            <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
              <button onClick={() => doMove("")} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors" data-testid="move-to-none">
                <Folder size={16} className="text-slate-400" /> Sin carpeta
              </button>
              {folders.map((f) => (
                <button key={f.id} onClick={() => doMove(f.id)} className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-left hover:bg-slate-100 dark:hover:bg-slate-800/60 transition-colors" data-testid={`move-to-${f.id}`}>
                  <Folder size={16} className="text-indigo-500" /> {f.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Visor PDF */}
      {viewer && (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex flex-col p-4 md:p-8" data-testid="pdf-viewer">
          <div className="flex items-center justify-between mb-3 text-white">
            <h3 className="font-heading font-medium text-sm truncate">{viewer.name}</h3>
            <div className="flex gap-2">
              <button onClick={() => navigate(`/editar-pdf/${viewer.id}`)} className="border border-white/30 rounded-lg p-2 hover:bg-white/10" title="Editar" data-testid="viewer-edit"><Pencil size={18} /></button>
              <button onClick={() => setViewer(null)} className="border border-white/30 rounded-lg p-2 hover:bg-white/10" data-testid="viewer-close"><X size={18} /></button>
            </div>
          </div>
          <iframe title={viewer.name} src={`${API}/docs/files/${viewer.id}/download?auth=${getToken()}`} className="flex-1 w-full rounded-lg bg-white" data-testid="viewer-iframe" />
        </div>
      )}
    </div>
  );
}

function FolderBtn({ active, onClick, icon: Icon, label, count, testid }) {
  return (
    <button onClick={onClick}
      className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${active ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 font-medium" : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"}`}
      data-testid={testid}>
      <span className="flex items-center gap-2.5 min-w-0"><Icon size={17} className="shrink-0" /> <span className="truncate">{label}</span></span>
      <span className="text-xs text-slate-400 font-mono2 shrink-0">{count}</span>
    </button>
  );
}

function RowMenu({ it, open, setOpen, onMove, onDelete, onDownload, onEdit }) {
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 opacity-60 group-hover:opacity-100 transition-opacity" data-testid={`menu-${it.id}`}>
        <MoreVertical size={16} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-44 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-2xl p-1.5 flex flex-col z-20">
            <button onClick={onEdit} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 text-left" data-testid={`action-open-${it.id}`}><Pencil size={15} /> Abrir / editar</button>
            <button onClick={onMove} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 text-left" data-testid={`action-move-${it.id}`}><FolderInput size={15} /> Mover a...</button>
            {it.type === "pdf" && (
              <button onClick={onDownload} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 text-left" data-testid={`action-download-${it.id}`}><Download size={15} /> Descargar</button>
            )}
            <div className="h-px bg-slate-200 dark:bg-slate-800 my-1" />
            <button onClick={onDelete} className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 text-left" data-testid={`action-delete-${it.id}`}><Trash2 size={15} /> Eliminar</button>
          </div>
        </>
      )}
    </div>
  );
}
